/**
 * Service to manage which highlights are visible/hidden
 * This allows users to toggle types and individual items on/off
 */

export class HighlightVisibilityService {
  private hiddenTypes: Set<string> = new Set();
  private hiddenItems: Set<string> = new Set();
  private listeners: Set<() => void> = new Set();

  /**
   * Toggle visibility of an entire type
   * @param type - The entity type to toggle
   */
  toggleType(type: string): void {
    if (this.hiddenTypes.has(type)) {
      this.hiddenTypes.delete(type);
    } else {
      this.hiddenTypes.add(type);
    }
    this.notifyListeners();
  }

  /**
   * Toggle visibility of a specific item
   * @param itemId - The item ID to toggle
   */
  toggleItem(itemId: string): void {
    if (this.hiddenItems.has(itemId)) {
      this.hiddenItems.delete(itemId);
    } else {
      this.hiddenItems.add(itemId);
    }
    this.notifyListeners();
  }

  /**
   * Check if a type is hidden
   * @param type - The entity type
   * @returns true if hidden, false if visible
   */
  isTypeHidden(type: string): boolean {
    return this.hiddenTypes.has(type);
  }

  /**
   * Check if an item is hidden
   * @param itemId - The item ID
   * @returns true if hidden, false if visible
   */
  isItemHidden(itemId: string): boolean {
    return this.hiddenItems.has(itemId);
  }

  /**
   * Check if an item should be visible (not hidden by type or individual toggle)
   * @param itemId - The item ID
   * @param type - The entity type
   * @returns true if visible, false if hidden
   */
  isVisible(itemId: string, type: string): boolean {
    return !this.isTypeHidden(type) && !this.isItemHidden(itemId);
  }

  /**
   * Get all hidden types
   * @returns Set of hidden type names
   */
  getHiddenTypes(): Set<string> {
    return new Set(this.hiddenTypes);
  }

  /**
   * Get all hidden items
   * @returns Set of hidden item IDs
   */
  getHiddenItems(): Set<string> {
    return new Set(this.hiddenItems);
  }

  /**
   * Clear all hidden types
   */
  clearHiddenTypes(): void {
    this.hiddenTypes.clear();
    this.notifyListeners();
  }

  /**
   * Clear all hidden items
   */
  clearHiddenItems(): void {
    this.hiddenItems.clear();
    this.notifyListeners();
  }

  /**
   * Reset all visibility settings
   */
  resetAll(): void {
    this.hiddenTypes.clear();
    this.hiddenItems.clear();
    this.notifyListeners();
  }

  /**
   * Subscribe to visibility changes
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Get statistics about hidden items
   */
  getStatistics(): {
    hiddenTypes: number;
    hiddenItems: number;
  } {
    return {
      hiddenTypes: this.hiddenTypes.size,
      hiddenItems: this.hiddenItems.size,
    };
  }
}

// Export singleton instance
export const highlightVisibilityService = new HighlightVisibilityService();

